import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Text } from '../../../../ui-library/typography/typography';
import type { UnifiedDocument } from './agent-document.helpers';
import {
  type DocumentActionHandlers,
  DocumentActions,
  DocumentSourceBadge,
  isFloraPreviewable,
} from './document-actions';

interface DocumentCardProps {
  doc: UnifiedDocument;
  handlers: DocumentActionHandlers;
  /** Primary click (Google → preview, Flora → download). */
  onPrimary: (doc: UnifiedDocument) => void;
}

/**
 * Grid-view card for a single document: type icon, name, type + modified meta,
 * and a footer with the source badge and source-specific actions. Clicking the
 * body triggers the document's primary action.
 *
 * @param {DocumentCardProps} props - The document, action handlers, and primary click.
 * @returns {JSX.Element} The document card.
 */
export function DocumentCard({ doc, handlers, onPrimary }: DocumentCardProps) {
  // Flora uses multicolor vscode-icons (no color override); Google uses
  // monochrome material-symbols that need the type color applied.
  const iconColor = doc.source === 'google' ? doc.typeColor : undefined;
  // Version chip: only a Flora-generated Word/Excel doc has browsable revisions,
  // and only when there is more than one (revision > 1) — a first-and-only
  // version has nothing to browse, so the chip (which opens the revision browser)
  // would be a dead end. Uploaded docs 400 on preview and are excluded.
  const showVersionChip = isFloraPreviewable(doc) && !!doc.flora && doc.revision != null && doc.revision > 1;

  return (
    <Card>
      <CardBody type="button" onClick={() => onPrimary(doc)}>
        <IconBox style={{ background: `${doc.typeColor}14` }}>
          <Icon icon={doc.typeIcon} width={28} height={28} color={iconColor} />
        </IconBox>
        <FileInfo>
          <FileName size="small" weight="medium" color="var(--ink-dark)" title={doc.name}>
            {doc.name}
          </FileName>
          <FileMeta>
            <Text size="tiny" weight="medium" color={doc.typeColor}>
              {doc.typeLabel}
            </Text>
            <Text size="tiny" color="var(--ink-lighter)">
              {doc.source === 'flora' && doc.sizeLabel !== '—' ? doc.sizeLabel : doc.modifiedLabel}
            </Text>
          </FileMeta>
        </FileInfo>
      </CardBody>
      <CardFooter>
        <DocumentSourceBadge doc={doc} />
        <FooterRight>
          {showVersionChip && (
            <RevisionToggle
              type="button"
              onClick={() => handlers.onPreviewFlora(doc)}
              aria-label={`Browse revisions of ${doc.name}`}
            >
              v{doc.revision}
              <Icon icon="material-symbols:history" width={13} height={13} />
            </RevisionToggle>
          )}
          <Actions>
            <DocumentActions doc={doc} handlers={handlers} />
          </Actions>
        </FooterRight>
      </CardFooter>
    </Card>
  );
}

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 14px;
  border-radius: 10px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover {
    border-color: var(--sky-base);
    box-shadow: 0 2px 6px var(--flora-shadow-card);
  }
`;

const CardBody = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  text-align: left;
  font: inherit;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
`;

const IconBox = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FileName = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--sky-lighter);
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RevisionToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px 6px;
  border-radius: 6px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-light);
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;

  &:hover {
    border-color: var(--sky-base);
    background: var(--sky-lighter);
    color: var(--ink-dark);
  }
`;
